/**
 * ChangeDetector Service - Zustand Store
 * Single Responsibility: Detect changes between data versions
 */

import { create } from 'zustand';

export interface ChangeDetectorStore {
  /**
   * Check if data changed
   */
  hasChanged: (before: any, after: any) => boolean;

  /**
   * Get list of changed field paths
   */
  getChangedFields: (before: any, after: any) => string[];

  /**
   * Get detailed change set with before/after values
   */
  getChangeSet: (
    before: any,
    after: any
  ) => Record<string, { before: any; after: any }>;

  /**
   * Helper: Get value at path
   */
  getValueAtPath: (obj: any, path: string) => any;
}

export const useChangeDetectorStore = create<ChangeDetectorStore>(
  (set, get) => ({
    hasChanged: (before: any, after: any): boolean => {
      if (before === after) return false;
      try {
        return JSON.stringify(before) !== JSON.stringify(after);
      } catch {
        // Circular reference or non-serializable — treat as changed
        return true;
      }
    },

    getChangedFields: (before: any, after: any): string[] => {
      const changes: string[] = [];

      const compare = (b: any, a: any, prefix = '') => {
        const keys = new Set([
          ...Object.keys(b || {}),
          ...Object.keys(a || {}),
        ]);

        keys.forEach((key) => {
          const path = prefix ? `${prefix}.${key}` : key;
          const beforeVal = b?.[key];
          const afterVal = a?.[key];

          try {
            if (JSON.stringify(beforeVal) !== JSON.stringify(afterVal)) {
              changes.push(path);
            }
          } catch {
            changes.push(path); // Non-serializable — treat as changed
          }

          if (typeof beforeVal === 'object' && typeof afterVal === 'object') {
            compare(beforeVal, afterVal, path);
          }
        });
      };

      compare(before, after);
      return changes;
    },

    getChangeSet: (
      before: any,
      after: any
    ): Record<string, { before: any; after: any }> => {
      const changeSet: Record<string, { before: any; after: any }> = {};
      const detector = get();
      const changedFields = detector.getChangedFields(before, after);

      changedFields.forEach((field) => {
        changeSet[field] = {
          before: detector.getValueAtPath(before, field),
          after: detector.getValueAtPath(after, field),
        };
      });

      return changeSet;
    },

    getValueAtPath: (obj: any, path: string): any => {
      return path
        .split(/[.\[\]]/)
        .filter(Boolean)
        .reduce((current, key) => current?.[key], obj);
    },
  })
);
